import { useState } from "react";
import { useLayout } from "@/components/layout/layout-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import { isTopLogoOnboardingLocked } from "@/desktop/onboarding-top-logo-lock";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { layoutMessages } from "@/i18n/messages/layout";
import { wallMessages } from "@/i18n/messages/wall";
import {
  createProjectFromUser,
  selectProjectFromUser,
} from "@/lib/select-project-space";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";
import type { WallScope } from "@/stores/wall-scope-store";
import { WallChromeButton } from "./wall-chrome-button";

const PROJECT: WallScope = { kind: "project" };
const ALL: WallScope = { kind: "all" };
const ARCHIVED: WallScope = { kind: "archived" };
const CHECK = <Icons.Check className="h-3.5 w-3.5" aria-hidden="true" />;

interface WallScopeMenuProps {
  scope: WallScope;
  onScopeChange: (scope: WallScope) => void;
}

/**
 * The header's scope menu (mockup `.scp`): Everything, the projects, each
 * with its colour and a check on the current one, Archived, then New
 * project and Manage projects. It replaces the rail's project switcher on the wall and
 * locks with it during onboarding.
 */
export function WallScopeMenu({ scope, onScopeChange }: WallScopeMenuProps) {
  const t = useT(wallMessages);
  const tFrames = useT(framesSharedMessages);
  const tLayout = useT(layoutMessages);
  const { openSettings } = useLayout();
  const [creating, setCreating] = useState(false);
  const projects = useProjectStore((state) => state.projects);
  const currentProject = useProjectStore((state) => state.currentProject);
  const step = useOnboardingStore((state) => state.step);
  const stepHydrated = useOnboardingStore((state) => state.stepHydrated);
  const locked = isTopLogoOnboardingLocked({ step, stepHydrated });
  const inProject = scope.kind === "project";
  const label =
    scope.kind === "project"
      ? (currentProject?.name ?? t("project"))
      : scope.kind === "all"
        ? t("everything")
        : tFrames("archived");

  const create = async () => {
    setCreating(true);
    try {
      const project = await createProjectFromUser("wall-project-create");
      if (!project) return;
      onScopeChange(PROJECT);
      openSettings({
        page: "projects-project",
        args: { projectId: project.id, title: project.name },
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WallChromeButton
          size="bar"
          className="max-w-[190px] shrink-0 pr-3 font-semibold text-primary"
          disabled={locked}
          aria-label={t("showingScope", { scope: label })}
          data-testid="wall-scope-menu"
        >
          <span className="min-w-0 truncate">{label}</span>
          <Icons.ChevronDown
            className="h-3.5 w-3.5 shrink-0 text-secondary"
            aria-hidden="true"
          />
        </WallChromeButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="max-h-96 min-w-56 max-w-72 overflow-y-auto"
        data-testid="wall-scope-menu-content"
      >
        <DropdownMenuItem
          icon={<Icons.Layers className="h-3.5 w-3.5" aria-hidden="true" />}
          trailing={scope.kind === "all" ? CHECK : undefined}
          onSelect={() => onScopeChange(ALL)}
          data-testid="wall-scope-everything"
        >
          {t("everything")}
        </DropdownMenuItem>
        {projects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            icon={
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: project.color }}
                aria-hidden="true"
              />
            }
            trailing={
              inProject && project.id === currentProject?.id ? CHECK : undefined
            }
            onSelect={() => {
              onScopeChange(PROJECT);
              selectProjectFromUser(project);
            }}
            data-testid={`wall-scope-project-${project.id}`}
          >
            {project.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          icon={<Icons.Archive className="h-3.5 w-3.5" aria-hidden="true" />}
          trailing={scope.kind === "archived" ? CHECK : undefined}
          onSelect={() => onScopeChange(ARCHIVED)}
          data-testid="wall-scope-archived"
        >
          {tFrames("archived")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<Icons.Plus className="h-3.5 w-3.5" aria-hidden="true" />}
          disabled={creating}
          onSelect={() => void create()}
          data-testid="wall-scope-new-project"
        >
          {tLayout("newProject")}
        </DropdownMenuItem>
        <DropdownMenuItem
          icon={<Icons.Settings className="h-3.5 w-3.5" aria-hidden="true" />}
          onSelect={() => openSettings({ page: "projects" })}
          data-testid="wall-scope-manage-projects"
        >
          {tLayout("manageProjects")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
